import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuilding,
  FaCrosshairs,
  FaExpand,
  FaImages,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaRoute,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "../HomePage/SectionTitle";
import MapComponent from "../../../components/MapComponent";

import { getAllInfrastructures } from "../../../services/infrastructuresServices";
import { resolveMediaUrl, truncateText } from "../HomePage/homeUtils";

const DEFAULT_MAP_QUERY = "Le Bouscat 33110";

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const parseCoordinates = (gps = "") => {
  if (!gps) return null;

  const [lat, lng] = String(gps)
    .split(",")
    .map((coord) => Number(String(coord).trim()));

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    latitude: lat,
    longitude: lng,
  };
};

const buildGoogleMapLink = (address, latitude, longitude) => {
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  const query = address || DEFAULT_MAP_QUERY;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query,
  )}`;
};

const getInfrastructurePhotos = (infrastructure) => {
  return [
    infrastructure.photo_url1,
    infrastructure.photo_url2,
    infrastructure.photo_url3,
    infrastructure.photo_url4,
  ].filter(Boolean);
};

const InfrastructuresPage = () => {
  useScrollToTop();

  const [infrastructures, setInfrastructures] = useState([]);
  const [selectedInfrastructureId, setSelectedInfrastructureId] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchInfrastructures = async () => {
      try {
        setLoading(true);

        const data = await getAllInfrastructures();

        const formattedInfrastructures = Array.isArray(data)
          ? data
              .map((infrastructure) => {
                const coordinates = parseCoordinates(
                  infrastructure.gps_infrastructure,
                );

                return {
                  ...infrastructure,
                  latitude:
                    infrastructure.latitude ??
                    infrastructure.lat ??
                    coordinates?.latitude ??
                    null,
                  longitude:
                    infrastructure.longitude ??
                    infrastructure.lng ??
                    coordinates?.longitude ??
                    null,
                };
              })
              .sort((a, b) => {
                const orderA = Number(a.ordre ?? 999);
                const orderB = Number(b.ordre ?? 999);

                if (orderA !== orderB) return orderA - orderB;

                return String(a.nom_infrastructure || "").localeCompare(
                  String(b.nom_infrastructure || ""),
                  "fr",
                  { sensitivity: "base" },
                );
              })
          : [];

        if (!mounted) return;

        setInfrastructures(formattedInfrastructures);

        const firstWithCoordinates = formattedInfrastructures.find(
          (item) =>
            Number.isFinite(Number(item.latitude)) &&
            Number.isFinite(Number(item.longitude)),
        );

        if (firstWithCoordinates) {
          setSelectedInfrastructureId(String(firstWithCoordinates.id));
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des infrastructures:",
          error,
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchInfrastructures();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredInfrastructures = useMemo(() => {
    const q = normalizeText(query.trim());

    if (!q) return infrastructures;

    return infrastructures.filter((infrastructure) => {
      const searchableText = [
        infrastructure.nom_infrastructure,
        infrastructure.adresse_infrastructure,
        infrastructure.description_infrastructure,
      ]
        .filter(Boolean)
        .join(" ");

      return normalizeText(searchableText).includes(q);
    });
  }, [infrastructures, query]);

  const selectedInfrastructure = useMemo(() => {
    return (
      infrastructures.find(
        (item) => String(item.id) === String(selectedInfrastructureId),
      ) || null
    );
  }, [infrastructures, selectedInfrastructureId]);

  const infrastructuresWithCoordinates = useMemo(() => {
    return filteredInfrastructures.filter(
      (infrastructure) =>
        Number.isFinite(Number(infrastructure.latitude)) &&
        Number.isFinite(Number(infrastructure.longitude)),
    );
  }, [filteredInfrastructures]);

  const resetFilters = () => {
    setQuery("");

    const firstWithCoordinates = infrastructures.find(
      (item) =>
        Number.isFinite(Number(item.latitude)) &&
        Number.isFinite(Number(item.longitude)),
    );

    setSelectedInfrastructureId(
      firstWithCoordinates ? String(firstWithCoordinates.id) : "",
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="h-[70px] w-full bg-slate-950" />

      <main>
        <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-24">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,197,94,0.2),transparent_35%),radial-gradient(circle_at_80%_35%,rgba(59,130,246,0.25),transparent_40%)]" />

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
              <Reveal>
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/90 backdrop-blur">
                    <FaBuilding />
                    Équipements municipaux
                  </span>

                  <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                    Infrastructures de la commune
                  </h1>

                  <p className="mt-6 max-w-3xl text-base leading-8 text-white/75 sm:text-lg">
                    Retrouvez les salles, stades, gymnases, équipements sportifs
                    et lieux utilisés par les associations.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
                  <HeroStat
                    icon={FaBuilding}
                    label="Infrastructures"
                    value={infrastructures.length}
                  />
                  <HeroStat
                    icon={FaMapMarkerAlt}
                    label="Affichées"
                    value={filteredInfrastructures.length}
                  />
                  <HeroStat
                    icon={FaCrosshairs}
                    label="Localisées"
                    value={infrastructuresWithCoordinates.length}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
          <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
            <SectionTitle
              eyebrow="Carte"
              title="Localisez les "
              highlight="infrastructures"
              subtitle="Sélectionnez un équipement dans la liste : la carte se recentre automatiquement sur son emplacement."
            />

            <div className="grid gap-6 xl:grid-cols-[400px_minmax(0,1fr)]">
              <Reveal>
                <aside className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-sm dark:border-white/10 dark:bg-slate-950">
                  <div className="border-b border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900">
                    <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                      Rechercher une infrastructure
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
                      Trouvez rapidement un lieu par nom, adresse ou
                      description.
                    </p>
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
                      <FaSearch className="text-slate-400" />
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher un lieu..."
                        className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
                      />
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
                      <FaMapMarkerAlt className="text-primary" />
                      <select
                        value={selectedInfrastructureId}
                        onChange={(e) =>
                          setSelectedInfrastructureId(e.target.value)
                        }
                        className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none dark:text-white"
                      >
                        <option value="">Choisir une infrastructure</option>
                        {filteredInfrastructures.map((infrastructure) => (
                          <option
                            key={infrastructure.id}
                            value={infrastructure.id}
                          >
                            {infrastructure.nom_infrastructure}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={resetFilters}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-white"
                    >
                      <FaTimes />
                      Réinitialiser
                    </button>

                    <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Résultats
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                        {filteredInfrastructures.length} infrastructure
                        {filteredInfrastructures.length > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  <div className="max-h-[720px] space-y-3 overflow-y-auto px-5 pb-5">
                    {loading ? (
                      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
                        Chargement des infrastructures...
                      </div>
                    ) : filteredInfrastructures.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
                        Aucune infrastructure trouvée.
                      </div>
                    ) : (
                      filteredInfrastructures.map((infrastructure) => (
                        <InfrastructureListItem
                          key={infrastructure.id}
                          infrastructure={infrastructure}
                          selected={
                            String(selectedInfrastructureId) ===
                            String(infrastructure.id)
                          }
                          onSelect={() =>
                            setSelectedInfrastructureId(
                              String(infrastructure.id),
                            )
                          }
                        />
                      ))
                    )}
                  </div>
                </aside>
              </Reveal>

              <Reveal delay={0.08}>
                <div className="grid gap-6">
                  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-sm dark:border-white/10 dark:bg-slate-950">
                    <div className="border-b border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                            Carte des infrastructures
                          </h2>
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                            {selectedInfrastructure
                              ? selectedInfrastructure.adresse_infrastructure ||
                                "Adresse non renseignée"
                              : "Sélectionnez une infrastructure pour la localiser."}
                          </p>
                        </div>

                        {selectedInfrastructure && (
                          <a
                            href={buildGoogleMapLink(
                              selectedInfrastructure.adresse_infrastructure,
                              Number(selectedInfrastructure.latitude),
                              Number(selectedInfrastructure.longitude),
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg"
                          >
                            <FaRoute />
                            Itinéraire
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="h-[620px] w-full">
                      {loading ? (
                        <div className="flex h-full items-center justify-center text-slate-500 dark:text-slate-300">
                          Chargement de la carte...
                        </div>
                      ) : infrastructuresWithCoordinates.length > 0 ? (
                        <MapComponent
                          infrastructures={infrastructuresWithCoordinates}
                          selectedInfrastructure={selectedInfrastructure}
                          onSelectInfrastructure={(infrastructure) =>
                            setSelectedInfrastructureId(
                              String(infrastructure.id),
                            )
                          }
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center p-8 text-center text-slate-500 dark:text-slate-300">
                          Aucune infrastructure avec coordonnées GPS.
                        </div>
                      )}
                    </div>
                  </div>

                  <InfrastructureDetail
                    infrastructure={selectedInfrastructure}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

function InfrastructureListItem({ infrastructure, selected, onSelect }) {
  const photos = getInfrastructurePhotos(infrastructure);
  const photoUrl = resolveMediaUrl(
    photos[0],
    "/img/placeholder-association.jpg",
  );

  const hasCoordinates =
    Number.isFinite(Number(infrastructure.latitude)) &&
    Number.isFinite(Number(infrastructure.longitude));

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "w-full overflow-hidden rounded-[1.5rem] border p-4 text-left transition",
        selected
          ? "border-primary bg-primary/5 shadow-lg"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:shadow-lg dark:border-white/10 dark:bg-slate-900",
      ].join(" ")}
    >
      <div className="flex gap-4">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-800">
          <img
            src={photoUrl}
            alt={infrastructure.nom_infrastructure}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
              Infrastructure
            </span>

            {!hasCoordinates && (
              <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                GPS manquant
              </span>
            )}
          </div>

          <h3 className="mt-2 text-base font-bold text-slate-950 dark:text-white">
            {infrastructure.nom_infrastructure}
          </h3>

          {infrastructure.adresse_infrastructure && (
            <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-300">
              {infrastructure.adresse_infrastructure}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function InfrastructureDetail({ infrastructure }) {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!infrastructure) {
    return (
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
        Sélectionnez une infrastructure pour afficher sa fiche.
      </div>
    );
  }

  const photos = getInfrastructurePhotos(infrastructure);
  const mainPhoto = resolveMediaUrl(
    photos[0],
    "/img/placeholder-association.jpg",
  );

  const openImage = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImage = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="border-b border-slate-200 p-5 dark:border-white/10">
          <h2 className="text-xl font-bold text-slate-950 dark:text-white">
            Détail de l’infrastructure
          </h2>
        </div>

        <div className="grid gap-6 p-5 lg:grid-cols-[340px_1fr]">
          <button
            type="button"
            onClick={() => openImage(mainPhoto)}
            className="group relative overflow-hidden rounded-[1.5rem] bg-slate-100 text-left dark:bg-slate-950"
            aria-label="Agrandir la photo principale"
          >
            <img
              src={mainPhoto}
              alt={infrastructure.nom_infrastructure}
              className="h-full min-h-[270px] w-full object-cover transition duration-500 group-hover:scale-105"
            />

            <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 opacity-0 transition group-hover:bg-slate-950/45 group-hover:opacity-100">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-primary shadow">
                <FaExpand />
              </span>
            </div>
          </button>

          <div>
            <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
              Équipement municipal
            </span>

            <h3 className="mt-4 text-3xl font-bold text-slate-950 dark:text-white">
              {infrastructure.nom_infrastructure}
            </h3>

            {infrastructure.description_infrastructure && (
              <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                {truncateText(infrastructure.description_infrastructure, 420)}
              </p>
            )}

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {infrastructure.adresse_infrastructure && (
                <InfoCard
                  icon={FaMapMarkerAlt}
                  label="Adresse"
                  value={infrastructure.adresse_infrastructure}
                />
              )}

              {infrastructure.gps_infrastructure && (
                <InfoCard
                  icon={FaCrosshairs}
                  label="Coordonnées GPS"
                  value={infrastructure.gps_infrastructure}
                />
              )}

              {photos.length > 0 && (
                <InfoCard
                  icon={FaImages}
                  label="Photos"
                  value={`${photos.length} photo${photos.length > 1 ? "s" : ""}`}
                />
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={buildGoogleMapLink(
                  infrastructure.adresse_infrastructure,
                  Number(infrastructure.latitude),
                  Number(infrastructure.longitude),
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                <FaRoute />
                Ouvrir l’itinéraire
              </a>
            </div>

            {photos.length > 1 && (
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.slice(1).map((photo, index) => {
                  const photoUrl = resolveMediaUrl(photo);

                  return (
                    <button
                      type="button"
                      key={`${photo}-${index}`}
                      onClick={() => openImage(photoUrl)}
                      className="group relative h-28 overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-950"
                      aria-label={`Agrandir la photo ${index + 2}`}
                    >
                      <img
                        src={photoUrl}
                        alt={`${infrastructure.nom_infrastructure} ${index + 2}`}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                      />

                      <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 opacity-0 transition group-hover:bg-slate-950/45 group-hover:opacity-100">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary shadow">
                          <FaExpand />
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedImage && (
        <ImageModal
          image={selectedImage}
          alt={infrastructure.nom_infrastructure}
          onClose={closeImage}
        />
      )}
    </>
  );
}

function ImageModal({ image, alt, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative max-h-[92vh] max-w-6xl">
        <img
          src={image}
          alt={alt || "Photo agrandie"}
          className="max-h-[92vh] rounded-3xl object-contain shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow transition hover:scale-105"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-md">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-primary shadow">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-white/55">
            {label}
          </p>
          <p className="mt-1 truncate text-lg font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
      <div className="flex gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-bold text-slate-700 dark:text-slate-200">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

export default InfrastructuresPage;
